import { Link } from 'react-router-dom';
import { api, fmt } from '../../api';
import { Empty, ErrorNote, Loading, PageHead, useLoad } from '../../components/ui';

export interface Problem {
  id: string;
  at: number;
  viewer: 'owner' | 'teammate' | 'demo' | 'prospect' | 'caller';
  stage: 'listen' | 'brain' | 'voice' | 'call' | 'preview' | 'server';
  provider: string;
  detail: string;
  shown: string;
  agentName: string | null;
  teammate: string | null;
  conversation_id: string | null;
  link_id: string | null;
}

const STAGE: Record<Problem['stage'], string> = {
  listen: 'Listening (speech to text)',
  brain: 'Brain (the AI model)',
  voice: 'Voice (text to speech)',
  call: 'Phone call',
  preview: 'Voice preview',
  server: 'This server',
};

const WHO: Record<Problem['viewer'], string> = {
  owner: 'You',
  teammate: 'Teammate',
  demo: 'Demo account',
  prospect: 'Prospect (demo link)',
  caller: 'Phone caller',
};

/**
 * Every failure someone met, with the real reason. Prospects, demo accounts
 * and callers saw only a plain sentence (shown under "They saw"); the
 * provider's own words are here, for whoever can fix it.
 */
export function ProblemsTable({ problems, owner = true }: { problems: Problem[]; owner?: boolean }) {
  return (
    <div className="tablewrap">
      <table>
        <thead>
          <tr>
            <th>When</th>
            <th>Who met it</th>
            <th>Where</th>
            <th>What went wrong</th>
            <th>They saw</th>
          </tr>
        </thead>
        <tbody>
          {problems.map((p) => (
            <tr key={p.id}>
              <td className="small">{fmt.date(p.at)}</td>
              <td className="small">
                {WHO[p.viewer] ?? p.viewer}
                {owner && p.teammate ? <div className="muted">{p.teammate}</div> : null}
                {p.agentName ? <div className="muted">{p.agentName}</div> : null}
              </td>
              <td className="small">
                {STAGE[p.stage] ?? p.stage}
                {p.provider ? <div className="muted">{p.provider}</div> : null}
              </td>
              <td className="small" style={{ maxWidth: 360, overflowWrap: 'anywhere' }}>
                {p.detail}
                {owner && p.conversation_id ? (
                  <div><Link to={`/conversations/${p.conversation_id}`}>Open the conversation</Link></div>
                ) : null}
              </td>
              <td className="small muted" style={{ maxWidth: 240 }}>{p.viewer === 'owner' || p.viewer === 'teammate' ? 'The same message' : p.shown || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Problems() {
  const { data, error, loading } = useLoad(() => api.get<{ problems: Problem[] }>('/api/owner/problems?limit=200'));
  return (
    <>
      <PageHead
        title="Problems"
        sub="What went wrong and why — a key refused, a model not found, a call that could not be placed. Visitors only ever see a plain sentence; the real reason is here. Kept for 30 days."
      />
      <ErrorNote error={error} />
      {loading && !data ? <Loading /> : !data?.problems.length ? <Empty>Nothing has gone wrong in the last 30 days.</Empty> : <ProblemsTable problems={data.problems} />}
    </>
  );
}
